import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { useReactToPrint } from "react-to-print";
import moment from 'moment';

import { AppConfig } from '../config/appConfig';

import { UtilityService } from '../services/utilityService';

import { ITransporter } from '../types/transporter';
import { IVendor } from '../types/vendor';
import { ICustomer } from '../types/customer';
import { ILoad } from '../types/load';
import { IMaterialSummary } from '../types/material';

import Image_Logo from "../assets/Logo.jpg";

import '../styles/billOfLading.scss';

export interface IBillOfLadingProps {
	load: ILoad;
	transporter?: ITransporter;
	vendor?: IVendor;
	customer?: ICustomer;
}

const BillOfLading: React.FunctionComponent<IBillOfLadingProps> = ({ load, transporter, vendor, customer }) => {
	const [materialSummaryList, setMaterialSummaryList] = useState<IMaterialSummary[]>([]);
	const [totalMaterialWeightLbs, setTotalMaterialWeightLbs] = useState(0);
	const [totalTareWeightLbs, setTotalTareWeightLbs] = useState(0);
	const [totalQuantity, setTotalQuantity] = useState(0);
	const ref = useRef(null);

	useEffect(() => {
		let summaryList: IMaterialSummary[] = [];
		let totalMaterialWeight: number = 0;
		let totalTareWeight: number = 0;
		let totalQuantity: number = 0;

		load?.materials?.map((listItem) => {
			const itemNetMaterialWeightInPounds = listItem.weight * (listItem.lbsConversion || 1);
			const itemContainerWeightInPounds = listItem.container.weight * (listItem.quantity ?? 1)

			const results = summaryList.filter((summaryItem) => listItem.materialId == summaryItem.materialId && listItem.materialPriceProfile == summaryItem.materialProfile);
			if (results.length == 0) {
				summaryList.push({
					"materialCategory": listItem.customerCategoryName,//This is mapping wrong. material category and customer category are NOT the same thing. 
					"quantity": listItem.quantity ?? 1,
					"materialName": listItem.materialName,
					"materialId": listItem.materialId,
					"materialProfile": listItem.materialPriceProfile,
					"materialWeightLbs": itemNetMaterialWeightInPounds,
					'isGrossWeightMaterial': listItem.materialPriceIsGrossWeightMaterial,
					"tareWeightLbs": listItem.materialPriceIsGrossWeightMaterial ? 0 : itemContainerWeightInPounds,
					"uomAbbreviation": listItem?.uomName
				})
			}
			else {
				results[0].quantity += listItem.quantity ?? 1;
				results[0].materialWeightLbs += itemNetMaterialWeightInPounds;
				results[0].tareWeightLbs += listItem.materialPriceIsGrossWeightMaterial ? 0 : itemContainerWeightInPounds;
			}
		});

		summaryList.map((item) => {
			totalMaterialWeight = totalMaterialWeight + item?.materialWeightLbs;
			totalTareWeight = totalTareWeight + item?.tareWeightLbs;
			totalQuantity = totalQuantity + item?.quantity;
		})

		setTotalMaterialWeightLbs(totalMaterialWeight);
		setTotalTareWeightLbs(totalTareWeight);
		setTotalQuantity(totalQuantity);
		setMaterialSummaryList(summaryList.sort((a, b) => UtilityService.compareTwoFields(a, b, 'materialName', 'materialProfile', 'string', 'number')));
	}, [load])

	const print = useReactToPrint({
		contentRef: ref,
		documentTitle: `BillOfLading`,
	});

	const handlePrintClick = () => {
		print();
	}

	return (
		<div className="hwi-bill-of-lading">
			<div className="print">
				<Button onClick={handlePrintClick}>Print Bill of Lading</Button>
			</div>
			<div className="wrapper" ref={ref}>
				<section className="content">
					{/* <article>This is an A4 document.</article> */}
					<div className="header">
						<div className="logo">
							<img src={Image_Logo}></img>
						</div>
						<div className="summary">
							<div className="title">HWI Environmental Technologies, Inc - Bill of Lading</div>
							<br></br>
							<div className="row">
								<div className="col">
									<p><strong>Trailer Number:</strong> {load?.trailerNumber ?? <span className="unavailable">Not Provided</span>}</p>
									<p><strong>Seal Number:</strong> {load?.sealNumber ?? <span className="unavailable">Not Provided</span>}</p>
									<p><strong>Carrier:</strong> {transporter?.details?.name ?? <span className="unavailable">Not Provided</span>}</p>
									<p><strong>Total Weight:</strong> {(totalMaterialWeightLbs + totalTareWeightLbs)?.toLocaleString()} lbs</p>
								</div>
								<div className="col">
									<p><strong>HWI Number:</strong> {load?.hwiNumber ?? <span className="unavailable">Not Provided</span>}</p>
									<p>
										<strong>Manifest Number{load?.manifestNumbers?.length !== 1 ? 's' : ''}:</strong>&nbsp;
										<span>
											{load?.manifestNumbers?.length > 0 ? (
												load.manifestNumbers.map((number, index) => {
													return <><span className='manifest-header-item' key={number}>{number}</span>{index < load.manifestNumbers.length - 1 ? ', ' : ''}</>;
												})
											) : (
												<span className="unavailable">None Provided</span>
											)}
										</span>
									</p>
									<p><strong>Scheduled On:</strong> {load?.scheduledPickupDate ? moment(load?.scheduledPickupDate).format("MM/DD/yyyy") : <span className="unavailable">Not Yet Scheduled</span>}</p>
									<p><strong>Shipped On:</strong> {load?.pickupDate ? moment(load?.pickupDate).format("MM/DD/yyyy") : <span className="unavailable">Not Yet Shipped</span>}</p>
								</div>

							</div>
						</div>
					</div>
					<div className="row parties">
						<div className="col consignee">
							<strong>Consignee:</strong>
							<p>{vendor?.details?.name ?? <span className="unavailable">No Name Provided</span>}</p>
							<p>{vendor?.physicalAddress?.street ?? <span className="unavailable">No Address Provided</span>}</p>
							{vendor?.physicalAddress?.street2 && <p>{vendor?.physicalAddress?.street2}</p>}
							<p>{vendor?.physicalAddress?.city ? vendor.physicalAddress.city + ', ' : ' '}{vendor?.physicalAddress?.state} {vendor?.physicalAddress?.zip}</p>
						</div>
						<div className="col shipper">
							<strong>Shipper:</strong>
							<p>HWI Environmental Technologies Inc</p>
							<p>6795 Langley Ave</p>
							<p>St. Louis, MO 63123</p>
							<p>(314) 842-8383 OR (800) 429-9783</p>
						</div>
					</div>
					<div className="materials">
						<div className="row header">
							<div className="col qty">QTY</div>
							<div className="col description">Material Description</div>
							<div className="col weight text-right">Material</div>
							<div className="col tare text-right">Tare</div>
							<div className="col total text-right">Total</div>
						</div>
						{materialSummaryList.map((item, index) => (
							<div className="row item" key={index}>
								<div className="col qty">{item?.quantity}</div>
								<div className="col description">{item?.materialName} {item?.materialProfile ? `#${item.materialProfile}` : ""}</div>
								<div className="col weight text-right">{(item?.materialWeightLbs).toLocaleString()} lbs</div>
								<div className="col tare text-right">
									{item?.isGrossWeightMaterial ? (
										<>*</>
									) : (
										<>{(item?.tareWeightLbs).toLocaleString()} lbs</>
									)}
								</div>
								<div className="col total text-right">{(item?.materialWeightLbs + item?.tareWeightLbs).toLocaleString()} lbs</div>
							</div>
						))}
						<div className="row totals">
							<div className="col qty">{totalQuantity}</div>
							<div className="col description"></div>
							<div className="col weight text-right">{totalMaterialWeightLbs?.toLocaleString()} lbs</div>
							<div className="col tare text-right">{totalTareWeightLbs?.toLocaleString()} lbs</div>
							<div className="col total text-right">{(totalMaterialWeightLbs + totalTareWeightLbs)?.toLocaleString()} lbs</div>
						</div>
						{materialSummaryList?.some(m => m.isGrossWeightMaterial) &&
							<div className="gross-weight-materials-warning">
								<p>{AppConfig.grossWeightMaterialMessage}</p>
							</div>
						}						
					</div>
					<div className="row source">
						<div className="col customer">
							<strong>Shipping From:</strong>
							<p>{customer?.details?.name}</p>
							<p>{customer?.shippingAddress?.street}</p>
							{customer?.shippingAddress?.street2 && <p>{customer?.shippingAddress?.street2}</p>}
							<p>{customer?.shippingAddress?.city}, {customer?.shippingAddress?.state} {customer?.shippingAddress?.zip}</p>
						</div>
						<div className="col section7">
							This shipment is to be delivered to consignee without recourse on the consigner shall sign the following statement.
							<br></br>
							The carrier shall not make delivery of this shipment without payment of freight and all lawfull charges.
							<br></br>
							I hereby certify that the contents of this shipment are fully and accurately described and are in all respects in proper condition for transport.  The materials described on this manifest are not subject to federal hazardous waste regulations.
						</div>
					</div>

					<div className="row signatures">
						<div className="col transporter">
							<strong>Transporter</strong>
							<p></p>
							<span>Signature</span>
							<span className="right">Date</span>
						</div>
						<div className="col consigner">
							<strong>Consigner</strong>
							<p></p>
							<span>Signature</span>
							<span className="right">Date</span>
						</div>
					</div>
					<div className="row legal">
						Received, subject to the classifications and tariffs in effect on the data of issue of the Shipping Order,
						the property described above in apparent good order, except as noted (contents and condition of contents of packages unknown), marked, consigned, and destined as indicated above, which said carrier (the word carrier being understood throughout this contract as meaning any person or corporation in possession of the propery under the contract) agrees to carry its usual place at said destination, if on its route otherwise to deliver to another carrier on the route to said destination it is mutually agreed, as to each carrier of all of any of said property over all or any portion of said route destination, and as to each party at any time interested in all or any of said property, that every service to be performed hereunder shall be subject to all the terms and conditions of the unirmo domestic straight bill of lading set forth (1) in official southern, western Illinois freight classifications in effect on the date hereof, if this is a rail or a rail-water shipment, or (2) in the applicable motor carrier classification or tariff if the is a motor carrier shipment.
						Shipper herby verifies that he is familiar with all the terms and conditions of the said bill of lading, set forth in the classification or tariff which governs the transportation of this shipment and the said terms and conditions are herby agreed by the shipper and accepted for himself and his assigns.
					</div>
					<div className="row reminder">
						Please retain one transporter signed BOL for the shipper. Provide one completed BOL to transporter.
					</div>
				</section>
			</div>
			{/* <Json data={materialSummaryList} title="Summary:"></Json>*/}
			{/* <Json data={load} title="Load:"></Json>  */}
		</div>
	)
}

export default BillOfLading;